<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import { tchMarked } from '@common/Common'
    import type { AlternativesComparisonDialogViewModel } from '@Alternatives/AlternativesLists/Components/AlternativesComparisonDialog'
    import type { AlternativesComparisonAlternativeReadModel, AlternativesComparisonAttributeReadModel } from '@Alternatives/AlternativesLists/Domain/readmodels'

    const props = defineProps<{
        viewModel: AlternativesComparisonDialogViewModel
    }>()

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.opened ?? false)
    const initialized = computed(() => vm.value.initialized)

    const data = computed(() => vm.value.data)

    const includeDescription = computed(() => data.value?.IncludeDescription ?? false)
    const includeStatus = computed(() => data.value?.IncludeStatus ?? false)

    const attributes = computed<AlternativesComparisonAttributeReadModel[]>(() =>
        data.value?.Attributes ?? []
    )

    const alternatives = computed<AlternativesComparisonAlternativeReadModel[]>(() =>
        data.value?.Alternatives ?? []
    )

    // ---------------- methods ----------------

    function getHtmlDescription(a: AlternativesComparisonAlternativeReadModel): string
    {
        return tchMarked(a.Description)
    }

    function getAttributeValue(a: AlternativesComparisonAlternativeReadModel, attrId: number): string
    {
        return a.Values.find(v => v.AttributeId === attrId)?.Value ?? ''
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Comparison</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading...</h2>

                        <div v-if="initialized">
                            <table class="table table-hover">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th v-for="a in alternatives" :key="a.Id">{{a.Name}}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="includeDescription === true">
                                        <th>Description</th>
                                        <td v-for="a in alternatives" :key="a.Id">
                                            <span v-html="getHtmlDescription(a)"></span>
                                        </td>
                                    </tr>
                                    <tr v-if="includeStatus === true">
                                        <th>Status</th>
                                        <td v-for="a in alternatives" :key="a.Id">
                                            {{a.StatusName}}
                                        </td>
                                    </tr>
                                    <tr v-for="attr in attributes" :key="attr.Id">
                                        <th>{{attr.Name}}</th>
                                        <td v-for="a in alternatives" :key="a.Id">
                                            {{getAttributeValue(a, attr.Id)}}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>